// Schema.org structured data generators for Afivio

export interface SchemaConfig {
  siteUrl?: string;
  siteName?: string;
}

type FAQItem = {
  question?: string;
  answer?: string;
  q?: string;
  a?: string;
};

const DEFAULT_CONFIG: SchemaConfig = {
  siteUrl: 'https://afivio.com',
  siteName: 'Afivio'
};

export function generateSoftwareApplicationSchema(params: Partial<SchemaConfig> & {
  description?: string;
  url?: string;
  applicationCategory?: string;
  operatingSystem?: string;
} = {}) {
  const { description, url, applicationCategory, operatingSystem, ...config } = params;
  const cfg = { ...DEFAULT_CONFIG, ...config };

  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": cfg.siteName,
    "applicationCategory": applicationCategory || "BusinessApplication",
    "operatingSystem": operatingSystem || "Web",
    ...(description ? { "description": description } : {}),
    "url": url || cfg.siteUrl,
    "provider": {
      "@type": "Organization",
      "name": cfg.siteName,
      "url": cfg.siteUrl
    }
  };
}

export function stripHtml(value: string) {
  return String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function generateFAQPageSchema(faqs: FAQItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question || faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": stripHtml(faq.answer || faq.a || '')
      }
    }))
  };
}

export function generateHowToSchema(params: {
  name: string;
  description: string;
  steps: Array<{ name: string; text: string }>;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": params.name,
    "description": params.description,
    "step": params.steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text
    }))
  };
}

export function generateArticleSchema(params: {
  headline: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  author: string;
  url: string;
  imageUrl?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": params.headline,
    "description": params.description,
    "datePublished": params.datePublished,
    "dateModified": params.dateModified || params.datePublished,
    "author": {
      "@type": "Person",
      "name": params.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Afivio",
      "logo": {
        "@type": "ImageObject",
        "url": "https://afivio.com/logo_main.png"
      }
    },
    "url": params.url,
    "image": params.imageUrl || "https://afivio.com/og.png"
  };
}

export function generateProductSchema(params: {
  name: string;
  description: string;
  price: string;
  priceCurrency: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": params.name,
    "description": params.description,
    "offers": {
      "@type": "Offer",
      "price": params.price,
      "priceCurrency": params.priceCurrency,
      "availability": "https://schema.org/InStock",
      "url": params.url
    }
  };
}
